import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { defaultPfpUrl } from "../utils";

/**
 * Founder / staff profile with Shadcn card and avatar.
 * @param {Object} props
 * @param {Object} props.staff
 * @param {string} props.staff.name
 * @param {string} props.staff.role
 * @param {string} props.staff.bio
 * @param {string} props.staff.imageUrl
 * @param {string} [props.staff.linkedInUrl]
 * @returns {JSX.Element}
 */
export default function StaffCard({ staff }) {
  const { name, role, bio, imageUrl, linkedInUrl } = staff;
  const [imgSrc, setImgSrc] = useState(imageUrl);

  const initials = name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const avatar = (
    <Avatar
      className={`mx-auto aspect-square h-48 w-48 border-4 border-staff-avatar-border shadow-staff-card ring-4 ring-staff-avatar-ring ${
        linkedInUrl ? "cursor-pointer" : ""
      }`}
    >
      <AvatarImage
        src={imgSrc}
        alt={`${name} — ${role}`}
        onLoadingStatusChange={(status) => {
          if (status === "error" && imgSrc !== defaultPfpUrl) {
            setImgSrc(defaultPfpUrl);
          }
        }}
      />
      <AvatarFallback className="text-lg font-semibold">{initials}</AvatarFallback>
    </Avatar>
  );

  return (
    <div className="w-full transition-transform duration-300 ease-out motion-safe:hover:-translate-y-0.5">
      <Card className="group/card w-full overflow-hidden border-border bg-primary text-center shadow-staff-card transition-shadow duration-300 ease-out motion-safe:hover:shadow-staff-card-hover">
        <CardHeader className="items-center pb-2 pt-8">
          <div className="group/avatar relative mb-2 origin-center transition-transform duration-300 ease-out motion-safe:group-hover/card:scale-[1.02] motion-safe:group-focus-within/card:scale-[1.02]">
            {linkedInUrl ? (
              <a
                href={linkedInUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
              >
                {avatar}
              </a>
            ) : (
              avatar
            )}
          </div>
          <CardTitle className="text-2xl text-text-primary">
            {linkedInUrl ? (
              <a
                href={linkedInUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-accent transition-colors"
              >
                {name}
              </a>
            ) : (
              name
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-2">
          <p className="text-lg font-semibold text-accent">{role}</p>
        </CardContent>
        <CardFooter className="justify-center px-4 pb-8 pt-0">
          <p className="mx-auto max-w-sm text-text-secondary leading-relaxed">
            {bio}
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
